import { useEffect } from "react";

/**
 * usePushToast
 *
 * Listens for foreground Firebase Cloud Messaging push messages
 * (i.e. when the browser tab is visible/open) and dispatches a
 * custom DOM event "push-toast" so that any mounted toast listener
 * (App.js) can show a toast without prop-drilling.
 *
 * Background messages (tab hidden / closed) are handled by the
 * firebase-messaging-sw.js service worker which shows a native
 * browser notification automatically.
 */
export function usePushToast() {
  useEffect(() => {
    let unsubscribe = null;

    const setupListener = async () => {
      try {
        const { getWebMessaging } = await import("../firebaseConfig");
        const { onMessage } = await import("firebase/messaging");

        const messaging = await getWebMessaging();
        if (!messaging) return;

        // onMessage fires ONLY when the app is in the foreground.
        unsubscribe = onMessage(messaging, (payload) => {
          const { title, body, icon } = payload.notification || {};
          const data = payload.data || {};

          // Dispatch a custom event so App.js (or any listener) can
          // show a toast without needing direct access to setState.
          window.dispatchEvent(
            new CustomEvent("push-toast", {
              detail: {
                title: title || data.title || "Notification",
                body: body || data.body || "",
                icon: icon || data.icon || null,
              },
            })
          );
        });
      } catch (e) {
        // Messaging not supported (e.g. iOS < 16.4, Firefox private mode)
      }
    };

    setupListener();

    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, []);
}
